import React from 'react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';

import { containerStyles } from '../../styles/layout';
import { body, headers } from '../../styles/type';
import { rem } from '../../utilities/style';
import { colors } from '../../theme/colors';
import { media } from '../../theme/media';

import Image from '../Image';
import TrialFormLoader from '../trialForm/TrialFormLoader';
import TextRenderer from '../TextRenderer';
import Link from '../Link';

const Container = styled.div`

${media('>small')} {
    padding: ${rem(80)} 0;
    background-image: linear-gradient(90deg, ${colors.white} 50%, ${colors.blue['50']} 50%);
  }
`;

const ContentWrapper = styled.div`
  ${containerStyles.regular}


  ${media('>small')} {
    display: flex;
    justify-content: space-between;
    gap: ${rem(80)};
  }
`;

const FormWrapper = styled.div`
  flex: 0 1 ${rem(560)};
`;

const SideContent = styled.div`
  background-color: ${colors.blue['50']};
  flex: 0 1 ${rem(560)};
  display: flex;
  flex-direction: column;

  ${media('<=small')} {
    padding-top: ${rem(40)};
    padding-bottom: ${rem(80)};
    padding-inline: ${rem(20)};
    margin-inline: ${rem(-20)};
  }
`;

const Headline = styled.h2`
  ${headers.h3}
  text-align: center;
  margin-bottom: ${rem(40)};
`;

const Reviews = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  column-gap: ${rem(40)};
  row-gap: ${rem(20)};
  margin-bottom: ${rem(40)};
`;

const ReviewWrapper = styled.div`
`;

const Review = styled(Image)`
  height: ${rem(45)};
  width: auto;
`;

const Badges = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  column-gap: ${rem(40)};
  row-gap: ${rem(20)};
  margin-bottom: ${rem(40)};
`;

const BadgeWrapper = styled.div`

`;

const Badge = styled(Image)`
  height: ${rem(75)};
  width: auto;
`;

const CardContent = styled.div`
  padding-top: ${rem(40)};
`;

const CardsHeadline = styled.p`
  ${headers.h6}
  margin-bottom: ${rem(16)};
`;

const CardsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${rem(16)};
`;

const CardTitle = styled(Link)`
  ${headers.h6}
  color: ${colors.blue['800']};
  margin-bottom: ${rem(4)};
  cursor: pointer;

  &::after {
    content: '';
    inset: 0;
    position: absolute;
  }
`;

const Card = styled.div`
  flex: 1 1 ${rem(200)};
  position: relative;
  padding: ${rem(16)};
  background-color: ${colors.white};
  border-radius: ${rem(8)};
  border: 1px solid ${colors.gray['200']};

  &:hover {
    ${CardTitle} {
      text-decoration: underline;
    }
  }
`;

const CardText = styled.p`
  ${body.small}
  color: ${colors.gray['600']};
`;

const TrialFormArea = ({
  trialForm,
  headline,
  reviews,
  badges,
  cardsHeadline,
  cards
}) => (
  <Container>
    <ContentWrapper>
      <FormWrapper>
        <TrialFormLoader variation={trialForm.variation} />
      </FormWrapper>
      <SideContent>
        {headline && (
          <Headline>
            <TextRenderer text={headline} />
          </Headline>
        )}
        {reviews && reviews.length > 0 && (
          <Reviews>
            {reviews.map((review, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <ReviewWrapper key={index}>
                <Review image={review} objectFit="contain" />
              </ReviewWrapper>
            ))}
          </Reviews>
        )}
        {badges && badges.length > 0 && (
          <Badges>
            {badges.map((badge, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <BadgeWrapper key={index}>
                <Badge image={badge} />
              </BadgeWrapper>
            ))}
          </Badges>
        )}
        {cards && cards.length > 0 && (
          <CardContent>
            {cardsHeadline && (
              <CardsHeadline>
                <TextRenderer text={cardsHeadline} />
              </CardsHeadline>
            )}
            <CardsWrapper>
              {cards.map((card, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <Card key={index}>
                  <CardTitle to={card.linkUrl}>
                    <TextRenderer text={card.headline} />
                  </CardTitle>
                  <CardText>
                    <TextRenderer text={card.text} />
                  </CardText>
                </Card>
              ))}
            </CardsWrapper>
          </CardContent>
        )}
      </SideContent>
    </ContentWrapper>
  </Container>
);

TrialFormArea.propTypes = {
  trialForm: PropTypes.shape({
    variation: PropTypes.string
  }),
  headline: PropTypes.string,
  reviews: PropTypes.arrayOf(
    PropTypes.shape({
      // eslint-disable-next-line react/forbid-prop-types
      gatsbyImageData: PropTypes.object
    })
  ),
  badges: PropTypes.arrayOf(
    PropTypes.shape({
      // eslint-disable-next-line react/forbid-prop-types
      gatsbyImageData: PropTypes.object
    })
  ),
  cardsHeadline: PropTypes.string,
  cards: PropTypes.arrayOf(
    PropTypes.shape({
      headline: PropTypes.string,
      text: PropTypes.string,
      linkUrl: PropTypes.string
    })
  )
};

export const query = graphql`
  fragment TrialFormAreaBlockFragment on ContentfulTrialFormArea {
    __typename
    trialForm {
      id
      variation
    }
    headline
    reviews {
      gatsbyImageData(height: 45, quality: 75)
      ...ImageAssetFragment
    }
    badges {
      gatsbyImageData(height: 75, quality: 75)
      ...ImageAssetFragment
    }
    cardsHeadline
    cards {
      headline
      text
      linkUrl
    }
    personalizations {
      ...PersonalizationFragment
    }
  }
`;

export default TrialFormArea;
